import Bootsplash from "@-/com.npmjs.react-native-bootsplash";
import { Platform } from "@-/com.npmjs.react-native";
import memoize, { memoizeClear } from "@-/com.npmjs.memoize";
import { boundedPromise, withDeadline } from "@-/dev.improve.haniverse.lib.deadline";
import { APPLICATION_VISUAL_READY_DEADLINE_MS } from "#application-visual-ready";

/**
 * How long the native `Bootsplash.hide` may take before the boundary stops waiting for it.
 *
 * The call resolves once the 250ms cross-dissolve has finished; a native module that never answers
 * (a broken link, a lost callback across a bridge reload) must not hold the reveal forever. Three
 * seconds is an order of magnitude above the animation. Expiry fails open, like a rejection.
 */
export const BOOTSPLASH_HIDE_DEADLINE_MS = 3000;

/**
 * How long `whenBootsplashRevealed` waits before failing open: the chrome-ready deadline plus the
 * hide deadline, plus a margin for loading the application module in front of them.
 */
const BOOTSPLASH_REVEALED_WAIT_DEADLINE_MS =
  APPLICATION_VISUAL_READY_DEADLINE_MS + BOOTSPLASH_HIDE_DEADLINE_MS + 10_000;

/**
 * The native splash covers the whole cold start, up to the first committed application frame.
 *
 * It is still not a loading indicator for remote data: what it waits on is one local signal
 * (`whenApplicationVisualReady`), raised once the app chrome has committed. Network, auth, and sync
 * stay out of the decision. The splash previously came down the moment the mount boundary's effect
 * ran, which published the local boot shell — chrome drawn from RN primitives, with hollow
 * placeholder boxes where the real tab icons belong — as a visible state on every launch. Waiting
 * for the committed frame is what keeps those placeholders behind the splash.
 *
 * Nothing here waits forever. The chrome marker is the fast path and in a healthy launch the only
 * one, but a signal that can only come from a component some route must remember to mount is a
 * readiness handshake, and every readiness handshake is bounded (see `lib/deadline`): the chrome
 * signal fails open after `APPLICATION_VISUAL_READY_DEADLINE_MS` and the native hide after
 * `BOOTSPLASH_HIDE_DEADLINE_MS`, each reported when it expires. Content is still free to suspend
 * below the chrome for as long as its own bounds allow; it never gates the splash.
 */
export type BootsplashRevealReason = "app-mount-boundary";

interface BootsplashRevealState {
  listeners: Set<() => void>;
  reveal: Promise<void> | null;
  revealedReason: BootsplashRevealReason | null;
  status: "visible" | "hiding" | "hidden";
}

// One state for the lifetime of the JS runtime (a cold launch shows exactly one splash). Memoized
// rather than a top-level `let` so tests can clear it between trees via `memoizeClear`.
const getBootsplashRevealState = memoize((): BootsplashRevealState => ({
  listeners: new Set(),
  reveal: null,
  revealedReason: null,
  status: "visible",
}));

export function isBootsplashRevealed(): boolean {
  return getBootsplashRevealState().status === "hidden";
}

export function getBootsplashRevealReason(): BootsplashRevealReason | null {
  return getBootsplashRevealState().revealedReason;
}

/**
 * Resolves after the native splash has finished hiding — failing open if no reveal completes
 * within {@link BOOTSPLASH_REVEALED_WAIT_DEADLINE_MS}.
 */
export function whenBootsplashRevealed(): Promise<void> {
  const state = getBootsplashRevealState();
  if (state.status === "hidden") {
    return Promise.resolve();
  }
  return boundedPromise(
    {
      deadline: BOOTSPLASH_REVEALED_WAIT_DEADLINE_MS,
      onDeadline: "resolve",
      operation: "native splash reveal",
    },
    (resolve) => {
      const listener = () => resolve();
      state.listeners.add(listener);
      return () => {
        state.listeners.delete(listener);
      };
    },
  );
}

/**
 * Hide the native splash exactly once. The returned promise settles only when native removal has
 * completed, failed, or outlived `BOOTSPLASH_HIDE_DEADLINE_MS`. Failure and expiry are fail-open:
 * they settle the local boundary so a broken splash module cannot permanently prevent the app from
 * mounting.
 */
export function revealBootsplash(reason: BootsplashRevealReason): Promise<void> {
  const state = getBootsplashRevealState();
  if (state.reveal !== null) {
    return state.reveal;
  }

  state.status = "hiding";
  state.revealedReason = reason;

  // `fade: true` cross-dissolves the native splash over 250ms instead of cutting to the shell on a
  // single frame, and the native promise resolves only once that animation has finished. Nothing
  // here waits on data — the splash still comes down the moment the local shell is ready — so the
  // fade costs boot latency only the length of its own animation, and it is what stops the handoff
  // reading as a flash. The shell underneath is laid out to match the real chrome, so there is no
  // jump to hide behind the dissolve.
  state.reveal = (
    Platform.OS === "web"
      ? Promise.resolve()
      : withDeadline(
          { deadline: BOOTSPLASH_HIDE_DEADLINE_MS, operation: "native Bootsplash.hide" },
          () => Promise.resolve().then(() => Bootsplash.hide({ fade: true })),
        )
  )
    .catch(() => {
      // Deliberately fail open (a rejection or the deadline); see the docblock.
    })
    .then(() => {
      state.status = "hidden";
      // Each listener's cleanup deletes it mid-iteration, which Set iteration allows.
      for (const listener of state.listeners) {
        listener();
      }
      state.listeners.clear();
    });

  return state.reveal;
}

/** Test-only: forget the reveal so the next rendered tree starts with the splash "up" again. */
export function resetBootsplashRevealForTests(): void {
  memoizeClear(getBootsplashRevealState);
}
